import { Link } from "react-router-dom";
import type {
  LaunchReadinessOutput,
  MonetisationModelOutput,
  ProToolType,
  SalesHeadlineOutput,
  ToolResultSummary,
} from "@/features/tools/proToolTypes";

type ProToolResultsListProps = {
  results: ToolResultSummary[];
  toolType?: ProToolType;
  emptyMessage?: string;
};

function formatDate(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function formatBand(value: string): string {
  return value.replace(/_/g, " ");
}

function formatStatusLabel(value: string): string {
  if (value === "complete") {
    return "Saved";
  }

  if (value === "failed") {
    return "Needs retry";
  }

  return value.charAt(0).toUpperCase() + value.slice(1);
}

function getOutcome(result: ToolResultSummary): string {
  if (result.status === "failed") {
    return "Result needs another run";
  }

  if (!result.output_payload || result.status !== "complete") {
    return formatStatusLabel(result.status);
  }

  if (result.tool_type === "launch_readiness_checker") {
    const output = result.output_payload as Partial<LaunchReadinessOutput>;

    if (
      typeof output.readinessScore === "number" &&
      typeof output.scoreBand === "string"
    ) {
      return `${output.readinessScore}/100 - ${formatBand(output.scoreBand)}`;
    }
  }

  if (result.tool_type === "monetisation_model_suggester") {
    const output = result.output_payload as Partial<MonetisationModelOutput>;

    if (typeof output.recommendedModel?.label === "string") {
      return output.recommendedModel.label;
    }
  }

  if (result.tool_type === "sales_page_headline_generator") {
    const output = result.output_payload as Partial<SalesHeadlineOutput>;

    if (typeof output.recommendedTopPick?.headline === "string") {
      return `Top headline: ${output.recommendedTopPick.headline}`;
    }
  }

  return "Saved result";
}

export function ProToolResultsList({
  results,
  toolType,
  emptyMessage = "Your saved Pro tool results will appear here.",
}: ProToolResultsListProps) {
  const filteredResults = toolType
    ? results.filter((result) => result.tool_type === toolType)
    : results;

  if (filteredResults.length === 0) {
    return (
      <div className="mt-5 rounded-lg border border-dashed border-slate-300 p-6 text-center text-sm leading-6 text-slate-600">
        {emptyMessage}
      </div>
    );
  }

  return (
    <ul className="mt-5 grid gap-3">
      {filteredResults.map((result) => (
        <li
          key={result.id}
          className="rounded-lg border border-slate-200 bg-white p-4"
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h4 className="min-w-0 break-words text-sm font-semibold text-slate-950">
                  {result.toolDisplayName}
                </h4>
                <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-600">
                  {formatStatusLabel(result.status)}
                </span>
              </div>
              <p className="mt-2 break-words text-sm font-semibold text-indigo-700">
                {getOutcome(result)}
              </p>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                <span>{formatDate(result.created_at)}</span>
                {result.projectTitle ? (
                  <span className="break-words">
                    Project: {result.projectTitle}
                  </span>
                ) : null}
                {result.blueprintTitle ? (
                  <span className="break-words">
                    Blueprint: {result.blueprintTitle}
                  </span>
                ) : null}
              </div>
              {result.status === "failed" ? (
                <p className="mt-2 text-sm text-red-700">
                  This saved result did not complete. Please try again from the
                  tool page.
                </p>
              ) : null}
            </div>
            <Link
              to={`/tools/results/${result.id}`}
              className="w-fit rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 max-sm:w-full max-sm:text-center"
            >
              View result
            </Link>
          </div>
        </li>
      ))}
    </ul>
  );
}
